import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ContentCard from '@components/common/ContentCard';
import {
  useSignatureCapture,
  useAcknowledgmentState,
  useSignatureDocument,
  useSubmitPromotedPhotos
} from '../../hooks';
import { AcknowledgmentStatement } from './AcknowledgmentStatement';
import { PrintedNameField } from './PrintedNameField';
import { SignatureCanvas } from './SignatureCanvas';
import { SignatureTimestamp } from './SignatureTimestamp';
import { SignatureModeToggle } from './SignatureModeToggle';
import { TypedSignature } from './TypedSignature';
import { SubmitButton } from './SubmitButton';
import type { EquipmentReviewData } from '../../types';
import { WORK_STATUS, EQUIPMENT_STATUS } from '../../utils';
import { useWorkOrderDocuments } from '@api/enterprise/workOrderDocuments';
import { useUserStore } from '@stores/userStore';
import type { SignatureMode } from '../../utils/signatureDocumentUtils';
import { useCompleteWorkOrder } from '@api/enterprise/workorder/mutations';
import { useWorkOrderDetailsUnfiltered } from '@api/enterprise/workorder/queries';
import { useAllowedEventTypes } from '@api/enterprise/eventType';
import { OrderDetailStatus } from '@api/enterprise/openapi/models';
import { useApiToast } from '@hooks/useApiToast';

type Props = {
  jobNumber: string;
  equipmentList: EquipmentReviewData[];
  isLocked: boolean;
};

export const CustomerAcknowledgment = ({
  jobNumber,
  equipmentList,
  isLocked
}: Props) => {
  const { t } = useTranslation('customerReview');
  const userName = useUserStore(state => state.userName) ?? '';
  const navigate = useNavigate();
  const { showSuccess } = useApiToast();

  const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);
  const [documentTimestamp, setDocumentTimestamp] = useState<string | null>(
    null
  );
  const [wasBypassed, setWasBypassed] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const [hasPriorSignature, setHasPriorSignature] = useState(false);
  const [wantsNewSignature, setWantsNewSignature] = useState(false);
  const hasLoadedInitialSignature = useRef(false);

  const { mutate: completeWorkOrder, isPending: isCompletingWorkOrder } =
    useCompleteWorkOrder(() => navigate(`/work/${jobNumber}`));

  const { data: allDetails = [] } = useWorkOrderDetailsUnfiltered(
    jobNumber,
    !isLocked
  );

  const { data: allowedCalibrationEvents = [] } = useAllowedEventTypes();

  const incompleteNonCalibrationDetails = useMemo(() => {
    return allDetails.filter(detail => {
      const isIncomplete = detail.status === OrderDetailStatus.Incomplete;
      const isCalibrationEvent =
        detail.relatedEventType &&
        allowedCalibrationEvents.includes(detail.relatedEventType);
      return isIncomplete && !isCalibrationEvent;
    });
  }, [allDetails, allowedCalibrationEvents]);

  const hasIncompleteNonCalibrationDetails =
    incompleteNonCalibrationDetails.length > 0;

  const {
    canvasRef,
    state: signatureState,
    mode: signatureMode,
    setMode: setSignatureMode,
    handleClear,
    handleDone,
    checkIsEmpty,
    setTypedSignature,
    confirmTypedSignature,
    loadExistingSignature
  } = useSignatureCapture();

  const { printedName, setPrintedName } =
    useAcknowledgmentState(signatureState);

  const { data: existingDocuments, isLoading: isLoadingDocuments } =
    useWorkOrderDocuments(jobNumber);

  const handleSignatureLoaded = useCallback(
    ({
      dataUrl,
      timestamp,
      mode,
      name
    }: {
      dataUrl: string;
      timestamp: string;
      mode: SignatureMode;
      name: string;
    }) => {
      if (name) setPrintedName(name);
      setSignatureMode(mode);
      setDocumentTimestamp(timestamp);
      loadExistingSignature(dataUrl, timestamp);
    },
    [setPrintedName, setSignatureMode, loadExistingSignature]
  );

  const {
    findSignatureDocument,
    loadSignatureFromDocument,
    submitSignatureDocument,
    submitBypassedSignature,
    isSubmittingDocument
  } = useSignatureDocument({
    jobNumber,
    userName,
    printedName,
    existingDocuments,
    onSignatureLoaded: handleSignatureLoaded
  });

  const { submitPhotos, isSubmitting: isSubmittingPhotos } =
    useSubmitPromotedPhotos({
      jobNumber,
      equipmentList
    });

  const allItemsComplete =
    equipmentList.length > 0 &&
    equipmentList.every(
      item =>
        item.workStatus === WORK_STATUS.Skipped ||
        (item.workStatus === WORK_STATUS.Complete &&
          item.eventStatus === EQUIPMENT_STATUS.Complete)
    );

  const canSubmit = allItemsComplete && !hasIncompleteNonCalibrationDetails;

  const hasSignature =
    signatureMode === 'draw' ? !isCanvasEmpty : !!signatureState.dataUrl;

  useEffect(() => {
    if (
      isLoadingDocuments ||
      !existingDocuments ||
      hasLoadedInitialSignature.current
    ) {
      return;
    }

    const signatureDocument = findSignatureDocument();
    if (!signatureDocument) return;

    const signatureText = signatureDocument.signatureText || '';
    const isBypassed = signatureText.startsWith('bypassed:');

    if (isBypassed) {
      if (isLocked) {
        setWasBypassed(true);
      }
      hasLoadedInitialSignature.current = true;
    } else if (isLocked && signatureDocument.fileAttachment) {
      loadSignatureFromDocument(signatureDocument);
      hasLoadedInitialSignature.current = true;
    } else if (!isLocked && signatureDocument.fileAttachment) {
      const [, name] = signatureText.split(':');
      if (name) {
        setPrintedName(name);
      }
      setHasPriorSignature(true);
      loadSignatureFromDocument(signatureDocument);
      hasLoadedInitialSignature.current = true;
    } else if (!isLocked && signatureText) {
      const [, name] = signatureText.split(':');
      if (name) {
        setPrintedName(name);
      }
      hasLoadedInitialSignature.current = true;
    } else if (signatureDocument.additionDate && isLocked) {
      setDocumentTimestamp(signatureDocument.additionDate);
    }
  }, [
    existingDocuments,
    isLoadingDocuments,
    isLocked,
    findSignatureDocument,
    loadSignatureFromDocument,
    setPrintedName
  ]);

  const handleCanvasEnd = useCallback(() => {
    setIsCanvasEmpty(checkIsEmpty());
  }, [checkIsEmpty]);

  const handleClearClick = useCallback(() => {
    handleClear();
    setIsCanvasEmpty(true);
  }, [handleClear]);

  const handleRequireNewSignature = useCallback(() => {
    setWantsNewSignature(true);
    setHasPriorSignature(false);
    handleClear();
    setIsCanvasEmpty(true);
    setPrintedName('');
    setDocumentTimestamp(null);
  }, [handleClear, setPrintedName]);

  const handleSubmitWithAcknowledgment = useCallback(async () => {
    if (!canSubmit || isCompletingWorkOrder || !hasSignature) return;

    const isKeepingPriorSignature = hasPriorSignature && !wantsNewSignature;

    if (!isKeepingPriorSignature) {
      let dataUrl: string | undefined;

      if (signatureMode === 'draw') {
        const capturedSignature = handleDone();
        dataUrl = capturedSignature?.dataUrl;
      } else {
        const confirmedSignature = confirmTypedSignature();
        dataUrl = confirmedSignature?.dataUrl;
      }

      if (dataUrl) {
        await submitSignatureDocument(dataUrl, signatureMode);
      }
    }

    await submitPhotos();

    showSuccess('customerAcknowledgment.signatureSubmitted', 'customerReview');

    completeWorkOrder({
      jobNumber,
      subStatus: undefined
    });
  }, [
    canSubmit,
    isCompletingWorkOrder,
    hasSignature,
    hasPriorSignature,
    wantsNewSignature,
    signatureMode,
    handleDone,
    confirmTypedSignature,
    submitSignatureDocument,
    submitPhotos,
    showSuccess,
    completeWorkOrder,
    jobNumber
  ]);

  const handleSubmitWithoutAcknowledgment = useCallback(async () => {
    if (!canSubmit || isCompletingWorkOrder) return;

    await submitBypassedSignature(isSkipped ? 'skipped' : 'bypassed');

    await submitPhotos();

    showSuccess('customerAcknowledgment.signatureSubmitted', 'customerReview');

    completeWorkOrder({
      jobNumber,
      subStatus: undefined
    });
  }, [
    canSubmit,
    isCompletingWorkOrder,
    isSkipped,
    submitBypassedSignature,
    submitPhotos,
    showSuccess,
    completeWorkOrder,
    jobNumber
  ]);

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />

      <ContentCard
        title={t('acknowledgment.title')}
        titleColor="primary"
        sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
      >
        <AcknowledgmentStatement />

        {wasBypassed ? (
          <Box
            sx={{
              mt: 3,
              p: 3,
              backgroundColor: 'warning.light',
              borderRadius: 1,
              textAlign: 'center'
            }}
          >
            <Typography color="warning.dark" fontWeight="medium">
              {t('acknowledgment.bypassedMessage')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              opacity: isSkipped ? 0.5 : 1,
              pointerEvents:
                isSkipped || (hasPriorSignature && !wantsNewSignature)
                  ? 'none'
                  : 'auto',
              transition: 'opacity 0.2s ease'
            }}
          >
            <PrintedNameField
              value={printedName}
              onChange={setPrintedName}
              disabled={
                isLocked ||
                isSubmittingDocument ||
                signatureState.isConfirmed ||
                isSkipped
              }
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('acknowledgment.signatureLabel')}
              </Typography>

              {!isLocked && (
                <SignatureModeToggle
                  mode={signatureMode}
                  onModeChange={setSignatureMode}
                  disabled={
                    isSubmittingDocument ||
                    signatureState.isConfirmed ||
                    isSkipped
                  }
                />
              )}
            </Box>

            {signatureMode === 'draw' ? (
              <SignatureCanvas
                canvasRef={canvasRef}
                isConfirmed={signatureState.isConfirmed}
                confirmedDataUrl={signatureState.dataUrl}
                onEnd={handleCanvasEnd}
                disabled={isLocked || isSkipped}
              />
            ) : (
              <TypedSignature
                name={printedName}
                onSignatureGenerated={setTypedSignature}
                disabled={isLocked || isSkipped}
              />
            )}

            <SignatureTimestamp timestamp={documentTimestamp} />

            {!isLocked && signatureMode === 'draw' && !isCanvasEmpty && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <button
                  type="button"
                  onClick={handleClearClick}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#666',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                >
                  {t('acknowledgment.clear')}
                </button>
              </Box>
            )}
          </Box>
        )}

        {!isLocked && hasPriorSignature && !wantsNewSignature && !isSkipped && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRequireNewSignature}
              sx={{ textTransform: 'none' }}
            >
              {t('acknowledgment.requireNewSignature')}
            </Button>
          </Box>
        )}

        {isLocked ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              p: 2,
              backgroundColor: 'action.disabledBackground',
              borderRadius: 1
            }}
          >
            <Typography color="text.secondary">
              {t('acknowledgment.workOrderLocked')}
            </Typography>
          </Box>
        ) : (
          <>
            {hasIncompleteNonCalibrationDetails && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {t('acknowledgment.incompleteDetailsError')}
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 2
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isSkipped}
                    onChange={e => setIsSkipped(e.target.checked)}
                  />
                }
                label={t('acknowledgment.skipLabel')}
                labelPlacement="start"
                sx={{
                  mr: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: 'text.secondary'
                  }
                }}
              />
            </Box>
            {isSkipped && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('acknowledgment.skippedMessage')}
              </Alert>
            )}
            <SubmitButton
              canSubmit={canSubmit}
              hasSignature={hasSignature}
              isSubmitting={
                isCompletingWorkOrder ||
                isSubmittingDocument ||
                isSubmittingPhotos
              }
              isSkipped={isSkipped}
              signaturePreviewUrl={signatureState.dataUrl}
              printedName={printedName}
              onSubmitWithAcknowledgment={handleSubmitWithAcknowledgment}
              onSubmitWithoutAcknowledgment={handleSubmitWithoutAcknowledgment}
            />
          </>
        )}
      </ContentCard>
    </Box>
  );
};
